import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { LoggingService } from './logging.service';
import { UserNotificationService } from './user-notification.service';
import { FinancialDatAdjustmentErrorMessage } from '../shared/_constants';
import { FinancialAdjustmentFilter } from '@models/financialAdjustmentfilter.model';
import { BDMatrixFilter } from '@models/bdMatrixFilter.model';
import { saveAs } from 'file-saver-es';
import { FilterResult } from '@models/filterResult';
import { MasterMatrix } from '@models/mastermatrix.model';
import { MatrixExcelDTO } from '@models/matrixExcelDTO.model';
import { firstValueFrom, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MasterMatrixBulkAcceptItem } from '../mystudies/analysis/master-matrix/mm-bulk-accept-item.model';
import { Report } from '../models/reportTypeEnum.model';

@Injectable({
  providedIn: 'root',
})
export class MasterMatrixService {
  masterMatrixRejectCount: number;
  masterMatrixacceptedCount: number;
  masterMatrixBlankOrNeutralCount: number;

  public isImportCompleted: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private http: HttpClient,
    private logger: LoggingService,
    private userNotificationService: UserNotificationService,
  ) {}

  parseErrorBlob(err: HttpErrorResponse): Observable<any> {
    if (err.status !== 0) {
      const reader: FileReader = new FileReader();

      const obs = Observable.create((observer: any) => {
        reader.onloadend = () => {
          if (reader.result !== '') {
            observer.error(JSON.parse(reader.result.toString()));
          } else {
            observer.error(err);
          }
          observer.complete();
        };
      });
      reader.readAsText(err.error);
      return obs;
    }
    return Observable.create((observer: any) => {
      observer.error(err);
    });
  }

  exportFinancialInformation(financialAdjFilter: FinancialAdjustmentFilter, reportType: Report) {
    this.logger.info(this.constructor.name, 'exportFinancialInformation', 'Start');

    const reportDTO = {
      financialAdjustmentFilterDTO: financialAdjFilter,
      studyId: financialAdjFilter.studyId,
      studySearchStrategyId: financialAdjFilter.studySearchStrategyId,
      reportType: reportType,
    };

    firstValueFrom(
      this.http
        .post('/api/mastermatrix/exportfinancialdata/', reportDTO, {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
          observe: 'response',
        })
        .pipe(catchError(this.parseErrorBlob)),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'exportFinancialInformation', e);

      if (e.status === 404) {
        this.userNotificationService.addNotification(
          `All financial information Excel file from master matrix screen failed to download for study ${financialAdjFilter.study.studyId}: No data found.`,
          true,
        );
      } else {
        this.userNotificationService.addNotification(
          `All financial information Excel file from master matrix screen failed to download for study ${financialAdjFilter.study.studyId}:${FinancialDatAdjustmentErrorMessage.ERROR}`,
          true,
        );
      }
    });
  }

  async uploadExcel(formdata: FormData, returnurl: string, studyId: number): Promise<boolean> {
    const result = await firstValueFrom(this.http.post('/api/mastermatrix/upload/', formdata))
      .then(() => {
        this.userNotificationService.addNotification(
          `Financial information Excel file from master matrix screen uploaded successfully for study ${studyId}.`,
          false,
        );
        this.isImportCompleted.emit(true);
        return true;
      })
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(
            `Financial information Excel file from master matrix screen failed to upload for study ${studyId}: No data found.`,
            true,
          );
          return null;
        } else {
          let resultMsg = FinancialDatAdjustmentErrorMessage[e.error.toString()];
          if (!resultMsg) {
            resultMsg = e.error.toString();
          }
          this.userNotificationService.addNotification(
            `Financial information Excel file from master matrix screen failed to upload for study ${studyId}:${resultMsg}`,
            true,
          );
        }
        return false;
      });
    return result;
  }

  async importMatrix(formdata: FormData, returnurl: string, studyId: number): Promise<boolean> {
    const fileName = formdata.get('fileName');

    const result = await firstValueFrom(this.http.post('/api/mastermatrix/importmatrix/', formdata))
      .then(() => {
        this.userNotificationService.addNotification(
          `Import Complete: Your file '${fileName}' has been successfully imported into Master Matrix for studyId  ${studyId}.`,
          false,
        );
        this.isImportCompleted.emit(true);
        return true;
      })
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(
            `Import Unsuccessful – to ensure a successful import, kindly verify the following:

            The file you're importing corresponds to the same Search Strategy used at the time of export - any changes to the Search Strategy since the export can lead to a failed import.
            The order and number of columns in your file exactly match those in the exported file.
            There are no duplicate rows with the same Company ID and Company Name in the file.
            No new companies have been added to the list after export for study ${studyId}: No data found.`,
            true,
          );
          return null;
        } else {
          let resultMsg = FinancialDatAdjustmentErrorMessage[e.error.toString()];
          if (!resultMsg) {
            resultMsg = e.error.toString();
          }
          this.userNotificationService.addNotification(
            `Import Unsuccessful – to ensure a successful import, kindly verify the following:

            The file you're importing corresponds to the same Search Strategy used at the time of export - any changes to the Search Strategy since the export can lead to a failed import.
            The order and number of columns in your file exactly match those in the exported file.
            There are no duplicate rows with the same Company ID and Company Name in the file.
            No new companies have been added to the list after export ${studyId}:${resultMsg}`,
            true,
          );
        }
        return false;
      });
    return result;
  }

  public async getMasterMatrix(filter: BDMatrixFilter): Promise<FilterResult> {
    const result = <FilterResult>await firstValueFrom(this.http.post<FilterResult>('/api/MasterMatrix/MasterMatrixResult/', filter)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetMasterMatrix', e);
    });

    return result;
  }

  public getMasterMatrixPageData(filter: BDMatrixFilter): Observable<FilterResult> {
    return this.http.post<FilterResult>('/api/MasterMatrix/MasterMatrixResult/', filter) as Observable<FilterResult>;
  }

  public async bulkAccept(bulkAcceptItems: MasterMatrixBulkAcceptItem[]): Promise<boolean> {
    this.logger.info(this.constructor.name, 'BulkAccept', 'Start', JSON.stringify(bulkAcceptItems));
    const saveResult: boolean = <boolean>await firstValueFrom(
      this.http.post<boolean>('/api/MasterMatrix/BulkAcceptComparable', bulkAcceptItems),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'BulkAccept', e);
    });
    return saveResult;
  }

  async saveMasterMatrixData(masterMatrix: MasterMatrix): Promise<void> {
    this.logger.info(this.constructor.name, 'saveMasterMatrixData', 'Start');
    await firstValueFrom(this.http.post<FilterResult>('/api/MasterMatrix/SaveMasterMatrix/', masterMatrix)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'saveMasterMatrixData', e);
    });
  }

  async saveEliminationCriteria(masterMatrix: MasterMatrix): Promise<void> {
    this.logger.info(this.constructor.name, 'saveEliminationCriteria', 'Start');
    await firstValueFrom(this.http.post<FilterResult>('/api/MasterMatrix/EliminationCriteria/', masterMatrix)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'saveEliminationCriteria', e);
    });
  }

  async exportMatrix(studyId: number): Promise<boolean> {
    const result = await firstValueFrom(
      this.http.get('/api/mastermatrix/ExportMatrix/' + studyId, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then((response) => {
        let filename = 'Temp_';
        const contentDispositionHeader = response.headers.get('Content-Disposition');

        if (contentDispositionHeader) {
          filename = contentDispositionHeader.split(';')[1].trim().split('=')[1];
        }

        const blob = new Blob([response.body], { type: response.body.type.toString() });
        saveAs(blob, filename);

        this.userNotificationService.addNotification(`Master Matrix Excel file for ${studyId}: Ready to download.`, false);
        return true;
      })
      .catch((e) => {
        this.logger.httpError(this.constructor.name, 'exportMatrix', e);
        if (e.status === 404) {
          this.userNotificationService.addNotification(`Master Matrix Excel file failed to download for study ${studyId}: No data found.`, true);
        }

        return false;
      });
    return result;
  }

  public async getMasterMatrixRejectedCount(StudySearchStrategyId: number): Promise<number> {
    const result = <number>await firstValueFrom(
      this.http.get<number>(`/api/MasterMatrix/GetMasterMatrixRejectedCount/${StudySearchStrategyId}`),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'getMasterMatrixRejectedCount', e);
    });
    this.masterMatrixRejectCount = result;
    return result;
  }

  public async getMasterMatrixAcceptedCount(StudySearchStrategyId: number): Promise<number> {
    const result = <number>await firstValueFrom(
      this.http.get<number>(`/api/MasterMatrix/GetMasterMatrixAcceptedCount/${StudySearchStrategyId}`),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'getMasterMatrixAcceptedCount', e);
    });
    this.masterMatrixacceptedCount = result;
    return result;
  }

  public async getMasterMatrixBlankOrNeutralCount(StudySearchStrategyId: number): Promise<number> {
    const result = <number>await firstValueFrom(
      this.http.get<number>(`/api/MasterMatrix/GetMasterMatrixBlankOrNeutralCount/${StudySearchStrategyId}`),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'getMasterMatrixBlankOrNeutralCount', e);
    });
    this.masterMatrixBlankOrNeutralCount = result;
    return result;
  }

  public async ReportPLISummary(financialAdjFilter: FinancialAdjustmentFilter, isFinal: boolean, columnSelections: any): Promise<boolean> {
    this.logger.info(this.constructor.name, 'ReportPLISummary', 'Start');

    const notificationBody = `Study ${financialAdjFilter.studyId} Financial Adjustments / PLI Summary ${isFinal ? 'Final' : 'Preliminary'} Report`;

    const reportParametersSearchStrategyDTO = {
      isFinal: isFinal,
      columnSelectionsDTO: columnSelections,
      studyId: financialAdjFilter.studyId,
      studySearchStrategyId: financialAdjFilter.studySearchStrategyId,
      financialAdjustmentFilterDTO: financialAdjFilter,
    };

    await firstValueFrom(
      this.http.post('/api/mastermatrix/PLISummaryReport/', reportParametersSearchStrategyDTO, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then()
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(`${notificationBody}: No data found.`, false);
        } else {
          this.logger.httpError(this.constructor.name, 'ReportPLISummary', e);
          let resultMsg = FinancialDatAdjustmentErrorMessage[e.error.toString()];
          if (!resultMsg) {
            resultMsg = e.error.toString();
          }
          this.userNotificationService.addUpdateNotification('ReportPLISummary', `${notificationBody}: ${resultMsg}`, true);
        }
      });

    return true;
  }

  public async ReportPLISummaryAutoAdjustments(
    financialAdjFilter: FinancialAdjustmentFilter,
    isFinal: boolean,
    columnSelections: any,
    testedPartyId: number,
  ): Promise<boolean> {
    this.logger.info(this.constructor.name, 'ReportPLISummaryAutoAdjustments', 'Start');

    const notificationBody = `Study ${financialAdjFilter.studyId} Financial Adjustments / PLI Summary - Auto Adjustments ${
      isFinal ? 'Final' : 'Preliminary'
    } Report`;

    const reportParametersSearchStrategyDTO = {
      isFinal: isFinal,
      columnSelectionsDTO: columnSelections,
      financialAdjustmentFilterDTO: financialAdjFilter,
      studyId: financialAdjFilter.studyId,
      studySearchStrategyId: financialAdjFilter.studySearchStrategyId,
      testedPartyId: testedPartyId,
    };

    await firstValueFrom(
      this.http.post('/api/mastermatrix/PLISummaryReportAutoAdjustments/', reportParametersSearchStrategyDTO, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then()
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(`${notificationBody}: No data found.`, false);
        } else {
          this.logger.httpError(this.constructor.name, 'ReportPLISummaryAutoAdjustments', e);
          let resultMsg = FinancialDatAdjustmentErrorMessage[e.error.toString()];
          if (!resultMsg) {
            resultMsg = e.error.toString();
          }
          this.userNotificationService.addUpdateNotification('ReportPLISummaryAutoAdjustments', `${notificationBody}: ${resultMsg}`, true);
        }
      });

    return true;
  }

  public async isMasterMatrixCompleted(searchStrategyId: number): Promise<boolean> {
    const result = <boolean>await firstValueFrom(this.http.get<boolean>(`/api/MasterMatrix/IsMasterMatrixCompleted/${searchStrategyId}`)).catch(
      (e) => {
        this.logger.httpError(this.constructor.name, 'IsMasterMatrixCompleted', e);
      },
    );

    return result;
  }

  IsMasterMatrixStatusCompleted(StudySearchStrategyId: number): Observable<boolean> {
    this.logger.info(this.constructor.name, 'IsMasterMatrixCompleted', 'Start');
    const url = `/api/MasterMatrix/IsMasterMatrixCompleted/${StudySearchStrategyId}`;

    return this.http.get<boolean>(url).pipe(
      catchError((error) => {
        this.logger.httpError(this.constructor.name, url, error);
        return throwError(() => error);
      }),
    );
  }

  public async reportDetailedPLISummary(studyId: number, studySearchStrategyId: number, testedPartyId: number): Promise<boolean> {
    this.logger.info(this.constructor.name, 'ReportDetailedPLISummary', 'Start');

    if (studyId === undefined || studySearchStrategyId === undefined) {
      // caller should be handling this now, but I'll go ahead and leave this in here...
      this.userNotificationService.addUpdateNotification(
        'ReportDetailedPLISummary',
        `Detailed PLI Summary: Detailed PLI Summary Report does not exist.`,
        true,
      );
      return true;
    }

    const notificationBody = `Study ${studyId} Detailed PLI Summary`;

    const reportDTO = { studyId, studySearchStrategyId, testedPartyId };

    await firstValueFrom(
      this.http.post('/api/MasterMatrix/DetailedPLISummaryReport/', reportDTO, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then()
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(`${notificationBody}: No data found.`, false);
        } else {
          this.logger.httpError(this.constructor.name, 'ReportDetailedPLISummary', e);
          this.userNotificationService.addUpdateNotification('ReportDetailedPLISummary', `${notificationBody}: ${e.message}`, true);
        }
      });
    return true;
  }

  public requestCompaniesDetailsReport(
    studyId: number,
    studySearchStrategyId: number,
    filingTypeId: number,
    comparableIds: number[],
  ): Observable<void> {
    const input = { studyId, studySearchStrategyId, filingTypeId, comparableIds };
    return this.http.post<void>('/api/MasterMatrix/CompaniesDetailsReport', input) as Observable<void>;
  }
}
